import React, { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";

type Props = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  axis?: `both` | `x` | `y`;
}

const DragScroll = React.forwardRef<HTMLDivElement, Props>(({ children, className = ``, style, axis = `both` }, forwardedRef) => {
  const ref = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(forwardedRef, () => ref.current as HTMLDivElement);

  const [isDragging, setIsDragging] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const pos = useRef({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const checkScrollable = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const scrollableX = axis !== `y` && el.scrollWidth > el.clientWidth;
    const scrollableY = axis !== `x` && el.scrollHeight > el.clientHeight;
    setIsScrollable(scrollableX || scrollableY);
  }, [axis]);

  // Re-check after every render (catches product add/remove)
  useLayoutEffect(() => {
    checkScrollable();
  });

  // Re-check on resize (catches window/widget resize without a React re-render)
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const ro = new ResizeObserver(checkScrollable);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScrollable]);

  const draggingRef = useRef(false);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isScrollable) {
      return;
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    // Clear any existing selection immediately (Safari fix)
    window.getSelection()?.removeAllRanges();

    draggingRef.current = true;
    setIsDragging(true);
    pos.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) {
      return;
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    const dx = e.clientX - pos.current.startX;
    const dy = e.clientY - pos.current.startY;

    if (axis !== `y`) {
      el.scrollLeft = pos.current.scrollLeft - dx;
    }
    if (axis !== `x`) {
      el.scrollTop = pos.current.scrollTop - dy;
    }
  };

  const stopDragging = () => {
    draggingRef.current = false;
    setIsDragging(false);
  };

  // Prevent text selection during drag via the selectstart event.
  // Safari ignores userSelect: 'none' applied reactively after mousedown,
  // but it does respect a cancelled selectstart event.
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const onSelectStart = (e: Event) => {
      if (draggingRef.current) {
        e.preventDefault();
      }
    };

    el.addEventListener(`selectstart`, onSelectStart);
    return () => el.removeEventListener(`selectstart`, onSelectStart);
  }, []);

  return (
    <div
      ref={ ref }
      className={ `${ className }${ isScrollable ? ` is-scrollable` : `` }` }
      style={ isDragging ? { ...style, userSelect: `none` } : style }
      onMouseDown={ onMouseDown }
      onMouseMove={ onMouseMove }
      onMouseUp={ stopDragging }
      onMouseLeave={ stopDragging }
    >
      { children }
    </div>
  );
});

DragScroll.displayName = `DragScroll`;

export default DragScroll;
